import type React from 'react';
import { useCallback } from 'react';

import {
  type SelectDirection,
  findEnabledIndex,
  isTypeaheadKey,
} from './selectLogic';
import type { SelectOptionDataType } from './types/SelectOptionDataType';

const PAGE_STEP = 10;

type SelectKeyboardOptions = {
  activeIndex: number;
  closeMenu: () => void;
  focusTrigger: () => void;
  isInteractive: boolean;
  isOpen: boolean;
  openMenu: (initialIndex?: number) => void;
  options: SelectOptionDataType[];
  runTypeahead: (character: string) => void;
  selectOption: (index: number) => void;
  selectedIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
};

export function useSelectKeyboard({
  activeIndex,
  closeMenu,
  focusTrigger,
  isInteractive,
  isOpen,
  openMenu,
  options,
  runTypeahead,
  selectOption,
  selectedIndex,
  setActiveIndex,
}: SelectKeyboardOptions) {
  const moveActive = useCallback(
    (step: SelectDirection) => {
      const start =
        activeIndex === -1
          ? step === 1
            ? 0
            : options.length - 1
          : activeIndex + step;
      const next = findEnabledIndex(options, start, step);
      if (next !== -1) setActiveIndex(next);
    },
    [activeIndex, options, setActiveIndex],
  );

  const movePage = useCallback(
    (step: SelectDirection) => {
      if (options.length === 0) return;
      const from =
        activeIndex === -1
          ? step === 1
            ? 0
            : options.length - 1
          : activeIndex;
      const target = Math.min(
        Math.max(from + step * PAGE_STEP, 0),
        options.length - 1,
      );
      const forward = findEnabledIndex(options, target, step);
      const next =
        forward !== -1
          ? forward
          : findEnabledIndex(options, target, step === 1 ? -1 : 1);
      if (next !== -1) setActiveIndex(next);
    },
    [activeIndex, options, setActiveIndex],
  );

  return useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!isInteractive) return;
      const { key } = event;

      if (key === 'Tab') {
        if (!isOpen) return;
        if (!event.shiftKey && activeIndex >= 0) {
          selectOption(activeIndex);
        } else {
          closeMenu();
        }
        return;
      }

      if (!isOpen) {
        if (key === 'Enter' || key === ' ' || key === 'ArrowDown') {
          event.preventDefault();
          openMenu();
          return;
        }
        if (key === 'ArrowUp') {
          event.preventDefault();
          openMenu(
            selectedIndex >= 0
              ? undefined
              : findEnabledIndex(options, options.length - 1, -1),
          );
          return;
        }
        if (key === 'Home' || key === 'End') {
          event.preventDefault();
          openMenu(
            findEnabledIndex(
              options,
              key === 'Home' ? 0 : options.length - 1,
              key === 'Home' ? 1 : -1,
            ),
          );
          return;
        }
        if (isTypeaheadKey(event)) {
          event.preventDefault();
          runTypeahead(key);
        }
        return;
      }

      switch (key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (event.altKey) {
            if (activeIndex >= 0) selectOption(activeIndex);
            else closeMenu();
          } else {
            moveActive(-1);
          }
          break;
        case 'Home':
        case 'End':
          event.preventDefault();
          setActiveIndex(
            findEnabledIndex(
              options,
              key === 'Home' ? 0 : options.length - 1,
              key === 'Home' ? 1 : -1,
            ),
          );
          break;
        case 'PageUp':
        case 'PageDown':
          event.preventDefault();
          movePage(key === 'PageUp' ? -1 : 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (activeIndex >= 0) selectOption(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          closeMenu();
          focusTrigger();
          break;
        default:
          if (isTypeaheadKey(event)) {
            event.preventDefault();
            runTypeahead(key);
          }
      }
    },
    [
      activeIndex,
      closeMenu,
      focusTrigger,
      isInteractive,
      isOpen,
      moveActive,
      movePage,
      openMenu,
      options,
      runTypeahead,
      selectOption,
      selectedIndex,
      setActiveIndex,
    ],
  );
}
